import { useEffect, useState } from 'react'

// Install-to-home-screen banner.
//   - Android / desktop Chrome: listens for `beforeinstallprompt` and surfaces
//     an Install button that calls .prompt().
//   - iOS Safari: shows static "Add to Home Screen" instructions, since iOS
//     doesn't expose the prompt API.
// User-dismissible; choice remembered in localStorage so we don't nag.

const DISMISS_KEY = 'signed-and-sealed-install-dismissed'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIosHint, setShowIosHint] = useState(false)
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1' } catch { return false }
  })

  useEffect(() => {
    if (dismissed) return

    // Don't show if running as installed PWA / standalone app
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    if (isStandalone) return

    const handler = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)

    // iOS detection — Safari only, no prompt API
    const isIos = /iPhone|iPad|iPod/.test(navigator.userAgent) && !(navigator as any).MSStream
    const isSafari = isIos && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS/.test(navigator.userAgent)
    if (isSafari) {
      // Show iOS hint after 6s so it's not too aggressive on first load
      const t = setTimeout(() => setShowIosHint(true), 6000)
      return () => { window.removeEventListener('beforeinstallprompt', handler); clearTimeout(t) }
    }

    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [dismissed])

  function dismiss() {
    setDeferred(null); setShowIosHint(false); setDismissed(true)
    try { localStorage.setItem(DISMISS_KEY, '1') } catch {}
  }

  async function install() {
    if (!deferred) return
    await deferred.prompt()
    const { outcome } = await deferred.userChoice
    if (outcome === 'accepted') dismiss()
    setDeferred(null)
  }

  if (!deferred && !showIosHint) return null

  return (
    <div style={{
      position: 'fixed', bottom: 'calc(var(--tab-h) + env(safe-area-inset-bottom) + 12px)',
      left: 12, right: 12, zIndex: 60,
      background: 'var(--ink)', color: '#fff', padding: '12px 16px', borderRadius: 'var(--radius-lg)',
      display: 'flex', alignItems: 'center', gap: 12, boxShadow: 'var(--shadow-lg)',
    }}>
      <div style={{ flex: 1, fontSize: 14 }}>
        {deferred ? (
          <>Install Signed and Sorted for one-tap launch and full offline use.</>
        ) : (
          <>To install: tap the <strong>Share</strong> icon then <strong>Add to Home Screen</strong>.</>
        )}
      </div>
      {deferred && (
        <button onClick={install} className="btn btn-primary btn-sm" style={{ width: 'auto' }}>Install</button>
      )}
      <button onClick={dismiss} aria-label="Dismiss"
        style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 22, padding: 4, cursor: 'pointer' }}>×</button>
    </div>
  )
}
